import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import Immutable from 'immutable';
import moment from 'moment';

import Context from '@bibliocommons/context';
import { TOGGLZ_FEATURES } from '@bibliocommons/constants-feature-togglz';

import ProgressBar from 'app/components/shared/ProgressBar';
import { HTTP_STATUS_CODE } from 'app/constants/ErrorConstants';
import { EVENTS_URL } from 'app/constants/EventsConstants';
import useFeature from 'app/hooks/useFeature';
import ErrorPage from 'app/components/ErrorPage';
import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import {
  selectEventSeriesEntities,
  selectEventAudienceEntities,
  selectEventTypesEntities,
  selectEventProgramsEntities,
  selectEventLanguagesEntities,
  selectImageEntities,
  selectLocationEntities,
  selectPlaceEntities
} from 'app/selectors/EntitiesSelector';
import { getEventsLinkedData } from 'app/helpers/events/eventsLinkedData';
import { mapDisplayBibToAnalyticsBibEntity } from 'app/helpers/displayBib';
import { formatDescription } from 'app/helpers/events/utils';
import { mapEventToEntity } from 'app/helpers/analytics/formatGA4Entity';
import { trackEventCalendarAdd, trackContentClick, trackContentExplore } from 'app/actions/AnalyticsGa4Actions';
import { METADATA_CATEGORY_TYPE, UI_COMPONENT_TYPE } from 'app/constants/AnalyticsGa4Constants';
import EventsDetailsPage from './EventsDetailsPage';

const EVENT = 'event';

export default function EventsDetailsPageContainer() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { mobileApp: isMobileApp } = useContext(Context);
  const isEventsV2DetailsPageEnabled = useFeature(TOGGLZ_FEATURES.EVENTS_V2_DETAILS_PAGE);
  const currentLibrary = useSelector(selectCurrentLibrary);
  const baseUrl = useSelector(state => state.getIn(['app', 'baseURL']));
  const event = useSelector(state => state.getIn(['events', 'eventsDetails', 'event']), Immutable.Map());
  const isFetching = useSelector(state => state.getIn(['events', 'eventsDetails', 'isFetching']));
  const series = useSelector(selectEventSeriesEntities);
  const eventTypes = useSelector(selectEventTypesEntities);
  const eventPrograms = useSelector(selectEventProgramsEntities);
  const eventAudiences = useSelector(selectEventAudienceEntities);
  const eventLanguages = useSelector(selectEventLanguagesEntities);
  const images = useSelector(selectImageEntities);
  const locations = useSelector(selectLocationEntities);
  const places = useSelector(selectPlaceEntities);
  const eventRoomBooking = useSelector(state => state.getIn(['entities', 'eventRoomBooking']));
  const catalogBibs = useSelector(state => state.getIn(['entities', 'catalogBibs']) ?? {});
  const eventRegistration = useSelector(state => state.getIn(['entities', 'eventRegistration']));
  const eventSettings = useSelector(state => state.getIn(['events', 'eventsDetails', 'settings']), Immutable.Map());
  const eventAccommodations =
    Object.values(useSelector(state => state.getIn(['entities', 'eventAccommodations'])?.toJS()))[0] ?? {};

  if (!isEventsV2DetailsPageEnabled) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  if (!event.size) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  const eventUrl = `${baseUrl}${EVENTS_URL}/${event?.get('id')}`;
  const eventTitle = event.getIn(['definition', 'title']);
  const description = formatDescription(event.getIn(['definition', 'description']));
  const isPast = moment(event.get('indexEnd')).isBefore(moment());
  const backToLinkHref = location.search ? `${location.pathname}${location.search}` : `${location.pathname}`;
  const registration = eventRegistration?.first();
  const eventSeries = series.get(event.get('seriesId'));
  const definition = event.get('definition', Immutable.Map());
  const featuredImage = definition.get('featuredImageId') && images.get(definition.get('featuredImageId'));
  const audiences = definition.get('audienceIds').map(id => eventAudiences.get(id));
  const languages = definition.get('languageIds').map(id => eventLanguages.get(id));
  const program = definition.get('programId') && eventPrograms.get(definition.get('programId'));
  const types = definition.get('typeIds').map(id => eventTypes.get(id));
  const eventLocation = definition.get('branchLocationId')
    ? locations?.get(definition.get('branchLocationId'))
    : places?.get(definition.get('nonBranchLocationId'));

  const linkedData = getEventsLinkedData({
    eventUrl,
    currentLibrary,
    event,
    eventLocation,
    languages,
    audiences,
    program,
    types,
    featuredImage
  });

  const trackAddToCalendarClick = ({ linkText, eventId }) => {
    dispatch(
      trackEventCalendarAdd({
        ui: {
          ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
          ui_component_label: linkText
        },
        eventId
      })
    );
  };

  const trackCatalogTitleClick = ({ displayBib }) => {
    const bib = mapDisplayBibToAnalyticsBibEntity(displayBib);

    dispatch(
      trackContentClick({
        bib,
        ui: {
          ui_component_type: UI_COMPONENT_TYPE.LINK_IMAGE
        },
        metadata: {
          metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_TITLE,
          metadata_value: displayBib.title
        }
      })
    );
  };

  const trackViewAllUpcomingEventsClick = ({ linkText }) => {
    dispatch(
      trackContentExplore({
        ui: {
          ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
          ui_component_label: linkText
        },
        event
      })
    );
  };

  const trackViewEventsInThisSeriesClick = ({ linkText }) => {
    dispatch(
      trackContentExplore({
        ui: {
          ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
          ui_component_label: linkText
        },
        event
      })
    );
  };

  const trackEventLocationNameClick = ({ linkText, locationData }) => {
    dispatch(
      trackContentExplore({
        ui: {
          ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
          ui_component_label: linkText
        },
        event,
        location: locationData
      })
    );
  };

  const trackSeriesEventLinkClick = ({ linkText, eventData }) => {
    dispatch(
      trackContentClick({
        event: mapEventToEntity({ event: eventData, series: eventSeries }),
        ui: {
          ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
          ui_component_label: linkText
        }
      })
    );
  };

  return (
    <div>
      <DocumentHeadContainer
        currentLibrary={currentLibrary}
        pageTitle={eventTitle}
        pageUrl={eventUrl}
        twitterUrl={eventUrl}
        description={description}
        linkedData={linkedData}
        noIndex={isPast}
        ogType={EVENT}
        imageUrl={featuredImage?.get('url')}
        unavailableAfter={event?.getIn(['definition', 'end'])}
      />
      <ProgressBar active={isFetching} />
      <EventsDetailsPage
        currentLibrary={currentLibrary}
        event={event}
        eventSeries={eventSeries}
        audiences={audiences}
        program={program}
        types={types}
        languages={languages}
        location={eventLocation}
        featuredImage={featuredImage}
        eventRoomBooking={eventRoomBooking}
        registration={registration}
        catalogBibs={catalogBibs}
        eventAccommodations={eventAccommodations}
        currentPageHref={backToLinkHref}
        eventSettings={eventSettings}
        isMobileApp={isMobileApp}
        trackAddToCalendarClick={trackAddToCalendarClick}
        trackCatalogTitleClick={trackCatalogTitleClick}
        trackEventLocationNameClick={trackEventLocationNameClick}
        trackSeriesEventLinkClick={trackSeriesEventLinkClick}
        trackViewAllUpcomingEventsClick={trackViewAllUpcomingEventsClick}
        trackViewEventsInThisSeriesClick={trackViewEventsInThisSeriesClick}
      />
    </div>
  );
}
